import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import Input from "../../Shared/Input";
import Toggle from "../../Shared/Toggle";
import PhoneNumberInput from "../../Shared/PhoneNumberInput";
import AddressLookupNative from "../../Shared/AddressLookupNative";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import { usePhoneNumber } from "../../../hooks/usePhoneNumber";
import { validatePhoneNumberWithCustomError, formatToE164 } from "../../../utility/phoneNumberUtils";
import {
  createTrimHandler,
  EMAIL_REGEX,
} from "../../../utility/formValidationUtils";
import {
  useBookingPage,
  useUpdateGeneralTab,
} from "../../../hooks/useBookingPage";

const ContactInformationForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Address lookup state — stores {id, name} object from AddressLookupNative
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Update mutation for general tab
  const { mutate: updateGeneral, isPending: isUpdating } =
    useUpdateGeneralTab();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      showContactInfo: false,
      contactPhone: "",
      contactEmail: "",
      contactAddress: "",
    },
  });

  // Watch form values
  const showContactInfo = watch("showContactInfo");

  // Use the phone number hook
  const { selectedCountry, onCountryChangeHandler, onInputChange, defaultCountry } = usePhoneNumber(
    configData?.data?.contactPhone
  );

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const formData = {
        showContactInfo: configData.data.showContactInfo || false,
        contactPhone: configData.data.contactPhone || "",
        contactEmail: configData.data.contactEmail || "",
        contactAddress: configData.data.contactAddress || "",
      };

      // Reset form and mark as pristine (not dirty)
      reset(formData, { keepDirty: false });

      // Restore address lookup display value when data loads
      if (configData.data.contactAddress) {
        setSelectedAddress({ id: null, name: configData.data.contactAddress });
      } else {
        setSelectedAddress(null);
      }
    }
  }, [configData, reset]);

  // Toggle change handler - only updates form state
  const handleToggleChange = (checked) => {
    setValue("showContactInfo", checked, { shouldDirty: true });
    setErrors({});
    setSuccess(false);
  };

  // Address lookup handler — stores the formatted address string in form state
  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    // address is {id, name} from SearchLookup, or null when the user clears the input
    setValue("contactAddress", address?.name ?? "", { shouldDirty: true });
  };

  const onSubmit = async (data) => {
    // Phone number validation for contact info section
    if (data.contactPhone) {
      const isValid = validatePhoneNumberWithCustomError(
        data.contactPhone,
        setErrors,
        defaultCountry
      );
      if (!isValid) {
        return;
      }
      data.contactPhone = formatToE164(data.contactPhone, defaultCountry);
    }

    await performSave(data);
  };


  const performSave = async (data) => {
    setErrors({});
    setSuccess(false);

    updateGeneral(data, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage =
          errorMessage || "Failed to update contact information";

        // Handle specific validation errors
        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Error",
          message: displayMessage,
        });
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={2} />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="px-4 py-6 sm:p-8">
        {/* Contact Info Toggle - at the top like BookingPageForm */}
        <div className="mb-6 pb-6 border-b border-ink-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Toggle checked={showContactInfo} onChange={handleToggleChange} />
              <span className="text-sm text-ink-600">
                {showContactInfo
                  ? "Contact information is visible to clients"
                  : "Contact information is hidden from clients"}
              </span>
            </div>
          </div>
        </div>

        {/* Contact fields - only show when toggle is on */}
        {showContactInfo && (
          <div className="space-y-6">
            <PhoneNumberInput
              selectedCountry={selectedCountry}
              onCountryChangeHandler={onCountryChangeHandler}
              onInputChange={onInputChange}
              optional={true}
              errormessage={errors?.contactPhone?.message}
              {...register("contactPhone", {
                onBlur: createTrimHandler(setValue, "contactPhone", true),
              })}
            />
            <Input
              label="Email Address"
              name="contactEmail"
              type="email"
              placeholder="your@email.com"
              errormessage={errors?.contactEmail?.message}
              {...register("contactEmail", {
                pattern: {
                  value: EMAIL_REGEX,
                  message: "Invalid email address",
                },
                onBlur: createTrimHandler(setValue, "contactEmail", true),
              })}
            />
            <div>
              <AddressLookupNative
                label="Address"
                onSelect={handleAddressSelect}
                placeholder="123 Main St, City, State"
                optional={true}
                defaultValue={selectedAddress}
              />
            </div>
          </div>
        )}
      </div>
      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Contact information updated successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default ContactInformationForm;
